import { useState } from 'react'
import { Link2, Search, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useUnresolvedAliases, useAccountSuggestions, useResolveAlias } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { useDebounce } from '@/hooks/use-debounce'
import { explainError } from '@/lib/explain-error'
import type { UnresolvedAlias } from '@/types'

/**
 * One queued name: its ranked candidates, and the choice.
 *
 * Candidates are only ever suggestions. On this data a similarity threshold
 * confidently picks "US Department of Transportation" for "Department of Transport
 * WA", so the score and the reason are both shown and nothing is preselected.
 */
function AliasRow({ alias }: { alias: UnresolvedAlias }) {
  const { toast } = useToast()
  const [override, setOverride] = useState('')
  const debouncedOverride = useDebounce(override, 300)
  const [chosen, setChosen] = useState<string | null>(null)

  const { data: suggestions, isLoading } = useAccountSuggestions(
    debouncedOverride.trim() || alias.alias
  )
  const resolve = useResolveAlias()
  const candidates = suggestions?.data ?? []

  const handleSave = async () => {
    if (!chosen) return
    try {
      await resolve.mutateAsync({ id: alias.id, account_id: chosen })
      toast({ title: 'Name linked', description: alias.alias })
    } catch (err) {
      toast({
        title: 'Could not save that match',
        description: explainError(err, 'Please try again.'),
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-1.5 border-b py-2.5 last:border-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-sm font-medium">{alias.alias}</span>
        {alias.reason && (
          <span className="flex items-start gap-1 text-xs text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warning" aria-hidden="true" />
            {alias.reason}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-start gap-2">
        <div className="relative min-w-[13rem] flex-1">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={override}
            onChange={(e) => setOverride(e.target.value)}
            placeholder="Search accounts..."
            className="h-8 pl-8 text-sm"
            aria-label={`Search for an account to link to ${alias.alias}`}
          />
        </div>
        <Button size="sm" onClick={handleSave} disabled={!chosen || resolve.isPending}>
          <Link2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Link
        </Button>
      </div>

      <fieldset className="space-y-0.5">
        <legend className="sr-only">Possible matches for {alias.alias}</legend>
        {isLoading ? (
          <p className="py-1 text-xs text-muted-foreground" role="status">
            Finding matches...
          </p>
        ) : candidates.length === 0 ? (
          <p className="py-1 text-xs text-muted-foreground">
            No similar accounts. Search above, or create the account first.
          </p>
        ) : (
          candidates.map((c) => (
            <label
              key={c.id}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-1.5 py-1 text-xs hover:bg-muted/50"
            >
              <input
                type="radio"
                name={`alias-${alias.id}`}
                value={c.id}
                checked={chosen === c.id}
                onChange={() => setChosen(c.id)}
                className="h-3.5 w-3.5"
              />
              <span className="flex-1">{c.name}</span>
              <span className="text-muted-foreground">{c.why}</span>
              <Badge
                variant={c.score >= 0.6 ? 'secondary' : 'outline'}
                className="w-11 justify-center tabular-nums"
              >
                {c.score.toFixed(2)}
              </Badge>
            </label>
          ))
        )}
      </fieldset>
    </div>
  )
}

/**
 * The queue of account names an importer could not match.
 *
 * Deliberately compact. This is a maintenance task that is usually empty, and it
 * sits above the worklist people actually came to use — so at rest it is a single
 * line, and it only expands when there is something to decide. It previously took
 * a full card and four lines of prose to say "nothing to do".
 */
export function AccountAliasResolver({ source }: { source?: string }) {
  const { data, isLoading } = useUnresolvedAliases(source)
  const aliases = data?.data ?? []
  // Collapsed at rest. The count in the header is what makes it discoverable, so
  // hiding the rows costs nothing and returns the space to the worklist.
  const [open, setOpen] = useState(false)

  if (isLoading) return null

  // Nothing to do: one quiet line, confirming the check ran.
  if (aliases.length === 0) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden="true" />
        All imported account names are matched.
      </p>
    )
  }

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
        <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="font-medium">Unmatched account names</span>
        <Badge variant="destructive">{aliases.length}</Badge>
        <span className="ml-auto hidden text-xs text-muted-foreground sm:inline">
          Suggestions are ranked, never applied automatically
        </span>
      </button>

      {open && (
        <div className="border-t px-3 pb-2">
          {aliases.map((a) => (
            <AliasRow key={a.id} alias={a} />
          ))}
        </div>
      )}
    </div>
  )
}
